import { Injectable } from '@angular/core';
import { InMemoryDbService, RequestInfo, getStatusText, STATUS } from 'angular-in-memory-web-api';
import { Observable } from 'rxjs';
import { PagedResult } from '@onrsr/ui/core';
import { GetOccurrences } from './in-memory-data/occurrences-data';
import { GetLineSections, GetLineSectionMetadata } from './in-memory-data/line-section-data';
import { GetDashboardData } from './in-memory-data/dashboard-data';
import { GetDefinedLocations, GetDefinedLocationMetadata } from './in-memory-data/defined-locations-data';
import { GetLevelCrossingSites, GetLevelCrossingSiteMetadata } from './in-memory-data/level-crossing-sites';
import { GetNotificationOfChangeData } from './in-memory-data/notifcation-of-change-data';
import { GetOrganisationProfile } from './in-memory-data/organisation-profile-data';
import { GetOnrsrPortalUsers } from './in-memory-data/onrsr-portal-user-data';
import { GetAnnualReturns } from './in-memory-data/annual-returns-data';
import { GetCategoryCSubmissions } from './in-memory-data/category-c-submissions-data';
import { GetOccurrenceExportData } from './in-memory-data/occurrence-export-data';
import { GetMonthlyReturns } from './in-memory-data/monthly-returns-data';

@Injectable({
  providedIn: 'root',
})
export class InMemoryDataService implements InMemoryDbService {

  createDb() {
    return {
      occurrences: GetOccurrences.occurrences,
      organisationProfiles: [GetOrganisationProfile.organisationProfile],
      onrsrPortalUsers: GetOnrsrPortalUsers.onrsrPortalUsers,
      lineSections: GetLineSections.lineSections,
      lineSectionMetadata: GetLineSectionMetadata.metadata,
      dashboardOpenTasks: GetDashboardData.openTasks,
      dashboardAwaitingSubmission: GetDashboardData.awaitingSubmission,
      dashboardInvolvedOccurrences: GetDashboardData.involvedOccurrences,
      dashboardDraftOccurrences: GetDashboardData.draftOccurrences,
      dashboardOverdueItems: GetDashboardData.overdueItems,
      dashboardUpcomingItems: GetDashboardData.upcomingItems,
      definedLocations: GetDefinedLocations.definedLocations,
      definedLocationMetadata: GetDefinedLocationMetadata.metadata,
      levelCrossingSites: GetLevelCrossingSites.levelCrossingSites,
      levelCrossingSiteMetadata: GetLevelCrossingSiteMetadata.metadata,
      notificationsOfChange: GetNotificationOfChangeData.notificationOfChangeData,
      annualReturns: GetAnnualReturns.annualReturns,
      categoryCSubmissions: GetCategoryCSubmissions.categoryCSubmissions,
      monthlyReturns: GetMonthlyReturns.monthlyReturns,
      occurrenceExportIncidentDetails: GetOccurrenceExportData.incidentDetails,
      occurrenceExportInvolvedOrganisations: GetOccurrenceExportData.involvedOrganisations,
      occurrenceExportTrainDetails: GetOccurrenceExportData.trainDetails,
      occurrenceExportChainOfEventDetails: GetOccurrenceExportData.chainOfEventDetails,
      occurrenceExportPersonConsequences: GetOccurrenceExportData.personConsequences,
      occurrenceExportDaTesting: GetOccurrenceExportData.daTesting,
    };
  }

  // Override GET to handle pagination parameters ourselves
  get(reqInfo: RequestInfo): Observable<unknown> | undefined {
    // Let passThruUnknownUrl handle requests for collections outside the in-memory db.
    if (!reqInfo.collection) {
      return undefined;
    }

    // Single-item lookup: match on either `id` (lowercase) or `Id` (PascalCase from .NET backend convention).
    if (reqInfo.id != null) {
      const collection = reqInfo.collection as Array<Record<string, unknown>>;
      const target = String(reqInfo.id);
      const item = collection.find((r) => String(r['id'] ?? r['Id'] ?? r['ONRSRRef'] ?? r['ONRSRRefId']) === target);

      if (item) {
        return reqInfo.utils.createResponse$(() => ({
          body: item,
          status: STATUS.OK,
          statusText: getStatusText(STATUS.OK),
        }));
      }
      return reqInfo.utils.createResponse$(() => ({
        body: { error: `Item ${target} not found` },
        status: STATUS.NOT_FOUND,
        statusText: getStatusText(STATUS.NOT_FOUND),
      }));
    }

    const collection = reqInfo.collection as unknown[];
    const query = reqInfo.query;

    // Parse pagination parameters (query.get() can return string or string[])
    const pageNumberParam = query?.get('pageNumber');
    const pageSizeParam = query?.get('pageSize');
    const pageNumber = parseInt(Array.isArray(pageNumberParam) ? pageNumberParam[0] : pageNumberParam ?? '1', 10);
    const pageSize = parseInt(Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam ?? '20', 10);

    // Calculate pagination
    const totalItems = collection.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const items = collection.slice(startIndex, endIndex);

    const pagedResult: PagedResult<unknown> = {
      items,
      totalItems,
      pageNumber,
      pageSize,
      totalPages,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
    };

    return reqInfo.utils.createResponse$(() => ({
      body: pagedResult,
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  // Override PUT so updates also match on PascalCase `Id` (default handler only checks `id`).
  put(reqInfo: RequestInfo): Observable<unknown> | undefined {
    if (!reqInfo.collection) {
      return undefined;
    }

    if (reqInfo.id == null) return undefined;

    const collection = reqInfo.collection as Array<Record<string, unknown>>;
    const target = String(reqInfo.id);
    const index = collection.findIndex((r) => String(r['id'] ?? r['Id'] ?? r['ONRSRRef'] ?? r['ONRSRRefId']) === target);

    if (index < 0) return undefined;

    const body = reqInfo.utils.getJsonBody(reqInfo.req) as Record<string, unknown>;
    collection[index] = { ...collection[index], ...body };

    return reqInfo.utils.createResponse$(() => ({
      body: collection[index],
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  post(reqInfo: RequestInfo): Observable<unknown> | undefined {
    if (!reqInfo.collection) {
      return undefined;
    }

    const collection = reqInfo.collection as Array<Record<string, unknown>>;
    const idKey = ['id', 'Id', 'ONRSRRef', 'ONRSRRefId'].find((key) => collection.some((r) => r[key] != null)) ?? 'Id';
    const nextId = collection.reduce((max, r) => Math.max(max, Number(r[idKey]) || 0), 0) + 1;

    const body = reqInfo.utils.getJsonBody(reqInfo.req) as Record<string, unknown>;
    const item = { ...body, [idKey]: nextId };
    collection.push(item);

    return reqInfo.utils.createResponse$(() => ({
      body: item,
      status: STATUS.CREATED,
      statusText: getStatusText(STATUS.CREATED),
    }));
  }
}
